import * as Yup from 'yup';

import { Column } from '~components/table-view-v3';

interface CategoryLookup {
  id: string;
  name: string;
  max_contrib_proportion?: number;
}

interface FundingSourceLookup {
  id: number;
  name: string;
}

const centeredClassNames = { header: 'header-align-center', cell: 'cell-align-center' };

const columnsFactory = (categories: CategoryLookup[] = [], fundingSources: FundingSourceLookup[] = []): Column[] => {
  const categoryOptions = categories.map((cat) => ({ id: cat.id, name: cat.name }));
  const fundingOptions = fundingSources.map((fund) => ({ id: fund.id, name: fund.name }));

  return [
    {
      id: 'category_id',
      header: 'Category',
      type: 'select',
      options: categoryOptions,
      isEditable: true,
      // When the category changes, snapshot the new max_contrib_proportion onto the row so
      // the Max Contribution column and the contribution validation reflect it immediately.
      onChange: (row) => {
        const catId = typeof row.category_id === 'string' ? row.category_id : '';
        const cat = categories.find((c) => c.id === catId);
        const max = cat?.max_contrib_proportion ?? 0;
        const existingCategory = typeof row.category === 'object' && row.category !== null ? row.category : {};
        return {
          category: { ...existingCategory, max_contrib_proportion: max },
        };
      },
      validation: Yup.string().required('Category is required'),
    },
    {
      id: 'funding_source_id',
      header: 'Funding Source',
      type: 'select',
      options: fundingOptions,
      isEditable: true,
      validation: Yup.number().typeError('Funding Source is required').required('Funding Source is required'),
    },
    {
      id: 'contrib_proportion',
      header: 'Contribution',
      type: 'number',
      classNames: centeredClassNames,
      isEditable: true,
      validation: Yup.number()
        .typeError('Contribution must be a number')
        .required('Contribution is required')
        .min(0, 'Contribution cannot be negative')
        .test('max-contrib', 'Contribution exceeds the category maximum', function (value) {
          if (value == null) return true;
          const max = this.parent?.category?.max_contrib_proportion;
          return max == null || value <= max;
        }),
    },
    {
      id: 'category.max_contrib_proportion',
      header: 'Max Contribution',
      type: 'number',
      classNames: centeredClassNames,
      isEditable: false,
      deriveValue: (row) => row.category?.max_contrib_proportion,
    },
    {
      id: 'start_date',
      header: 'Start Date',
      type: 'date',
      isEditable: true,
      validation: Yup.string().required('Start date is required'),
    },
    {
      id: 'end_date',
      header: 'End Date',
      type: 'date',
      isEditable: true,
      validation: Yup.string()
        .required('End date is required')
        .test('end-after-start', 'End date must be after start date', function (value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;
          return new Date(value) >= new Date(start_date);
        }),
    },
  ];
};

export default columnsFactory;
